import { useCallback, useMemo } from "react";
import { EventDateType, EventType } from "globals/types/models.ts";
import dayjs from "dayjs";
import { getUrlFromFormat } from "globals/functions/image.ts";
import { getClosestNextDate } from "utils/data/event.ts";
import { useTranslation } from "react-i18next";
import { LANGS } from "globals/constants.ts";
import { useAppSelector } from "config/store";

function EventCard(props: {
  event: EventType,
  onClick: (event: EventType) => void
  children?: React.ReactNode
}) {
  const { event, onClick, children } = props;

  const { t } = useTranslation();
  const currentLang = useAppSelector(state => state.global.currentLang);

  const isPast = useMemo(() => {
    if (event.dates.length === 0) return false;
    const lastDate = event.dates[event.dates.length - 1];//last date must be in the past
    return dayjs().isAfter(lastDate.startDate, "day");

  }, [event.dates]);

  const startDateStr = useMemo(() => {
    if (event.dates.length === 0) return null;
    const closestDate = getClosestNextDate<EventDateType>(event.dates);
    if (!closestDate) {
      return null;
    }
    return dayjs(closestDate.startDate).format("LL");
  }, [event.dates]);

  const localOnClick = useCallback(() => {
    onClick(event);
  }, [onClick, event]);

  const imageUrl = useMemo(() => {
    const firstImage = event.images.length > 0 ? event.images[0] : null;
    let imageUrl = firstImage ? firstImage.imageUrl : null;
    if (firstImage && imageUrl) {
      if (firstImage.formats) {
        if (firstImage.formats.length > 0) {
          imageUrl = getUrlFromFormat(imageUrl, firstImage.formats, 512);//124px
        }
      }
    }
    return imageUrl;
  }, [event]);

  const recurringTitle = useMemo(() => {
    if (!event.recurring) return null;
    if (event.recurring === "none") return null;
    if (event.recurring === "daily") return t("event.recurring.repeatDaily");
    if (event.recurring === "weekly") return t("event.recurring.repeatWeekly");
    if (event.recurring === "bi-weekly") return t("event.recurring.repeatBiWeekly");
    if (event.recurring === "monthly") return t("event.recurring.repeatMonthly");
    return null;
  }, [t, event]);


  const eventTitle = currentLang === LANGS.FR ? event.titleFr : event.titleEn ? event.titleEn : event.titleFr;

  return (
    <div
      className={`w-full cursor-pointer relative rounded-md p-2 flex flex-row sm:flex-col justify-start items-center sm:items-start gap-2 ${isPast ? "bg-gray-50" : "border border-green-500 bg-green-150"}`}
      onClick={localOnClick}
    >
      <div className="h-14 w-14 min-w-14 sm:h-[184px] sm:w-full overflow-hidden rounded-md">
        {imageUrl ? (
          <img src={imageUrl} className="block h-full w-full object-cover object-center" />
        ) : (
          <div className="flex bg-gray-100 h-full w-full flex-col items-center justify-center">
            <span className="text-center text-xxs text-gray-400">
              {t("forms.noImage")}
            </span>
          </div>
        )}
      </div>
      <div className="flex flex-col justify-start items-start gap-1">
        <h3 className=" text-lg leading-none font-medium">
          {eventTitle}
        </h3>
        {startDateStr && (
          <p className=" text-xs">
            {startDateStr}
          </p>
        )}
        {recurringTitle && (
          <p className=" text-xs">
            {recurringTitle}
          </p>
        )}
        {children}
      </div>
    </div>
  );
}

export default EventCard;
